/**
 * Adapter layer: API shapes → view-model shapes.
 *
 * El backend (@champe-cm/db / @champe-cm/api) usa shapes que reflejan el
 * schema Drizzle. La SPA tiene view models distintos (slaRemainingSec, unread,
 * direction in/out, etc.). Acá hacemos el mapping en un solo lugar para
 * que los hooks queden limpios y se cambie el shape DB sin tocar la UI.
 */
import type {
  ClientSlug,
  Conversation,
  ConversationState,
  CurrentUser,
  Message,
  Platform,
  UserRole,
  AlertEvent,
  AuditEntry,
  Account,
  SandboxRun,
} from '@/types'

// ─── User ──────────────────────────────────────────────────────────────────

// Backend devuelve role en español. View model usa inglés (legacy SPA copy).
const ROLE_API_TO_FRONT: Record<string, UserRole> = {
  admin: 'admin',
  operador: 'operator',
  cliente: 'client',
  externo: 'external',
}

export interface ApiUser {
  id: string
  email: string
  name: string | null
  role: 'admin' | 'operador' | 'cliente' | 'externo'
  clientSlugs: string[]
}

export function apiUserToCurrentUser(u: ApiUser): CurrentUser {
  return {
    id: u.id,
    email: u.email,
    name: u.name ?? u.email,
    role: ROLE_API_TO_FRONT[u.role] ?? 'external',
    clientAccess: u.clientSlugs as ClientSlug[],
  }
}

// ─── Conversation ──────────────────────────────────────────────────────────

// Backend state: bot | human_taken | escalated | closed
// Front state:   bot | human       | escalated | closed
const STATE_API_TO_FRONT: Record<string, ConversationState> = {
  bot: 'bot',
  human_taken: 'human',
  escalated: 'escalated',
  closed: 'closed',
}

export interface ApiConversation {
  id: string
  clientSlug: string
  userExternalId: string
  userDisplayName: string | null
  platforms: string[]
  firstMessageAt: string
  lastMessageAt: string
  messageCount: number
  state: 'bot' | 'human_taken' | 'escalated' | 'closed'
  currentHumanId: string | null
  summary: string | null
  summaryUpdatedAt: string | null
  /** Última vez que un humano abrió/revisó la conv. Null = nunca leída. */
  lastReadAt?: string | null
  /** Foto de perfil del usuario externo (enriquecida via Meta). Null si no hay. */
  userAvatarUrl?: string | null
  createdAt: string
  updatedAt: string
  /** Provisto por el list endpoint para el preview del inbox. Null si no hay mensajes. */
  lastMessageBody?: string | null
  lastMessageKind?: string | null
  /** SLA per cliente/plataforma (de cm_toggles). Null = usar default del adapter. */
  slaDmSeconds?: number | null
  slaCommentSeconds?: number | null
}

// SLA default por kind cuando cm_toggles no tiene valores seteados.
// El backend resuelve sla_dm_seconds / sla_comment_seconds y los pasa en
// el item de lista. El adapter usa el correcto según kind del último mensaje.
const DEFAULT_SLA_DM_SEC = 30 * 60
const DEFAULT_SLA_COMMENT_SEC = 2 * 60 * 60

export function apiConversationToConversation(
  c: ApiConversation,
  lastMessageBody?: string,
): Conversation {
  const lastMs = new Date(c.lastMessageAt).getTime()
  const elapsed = (Date.now() - lastMs) / 1000
  // SLA cap depende del kind del último mensaje (DM más estricto que comment).
  // Si lastMessageKind no viene (fallback al detail endpoint que no lo trae),
  // asumimos DM por ser el caso más conservador.
  const kind = c.lastMessageKind ?? 'dm'
  const slaCap =
    kind === 'comment'
      ? (c.slaCommentSeconds ?? DEFAULT_SLA_COMMENT_SEC)
      : (c.slaDmSeconds ?? DEFAULT_SLA_DM_SEC)
  const slaRemainingSec = Math.round(slaCap - elapsed)

  return {
    id: c.id,
    client: c.clientSlug as ClientSlug,
    platform: (c.platforms[0] as Platform) ?? 'instagram',
    externalUserId: c.userExternalId,
    // Si el webhook capturó el handle/nombre lo usamos. Cuando solo tenemos
    // el PSID crudo (Instagram DM sin Graph enrichment), caemos al id externo
    // — feo pero único, mejor que un placeholder.
    externalUserName:
      c.userDisplayName ??
      (c.userExternalId.includes(':')
        ? c.userExternalId.split(':').slice(1).join(':')
        : c.userExternalId),
    externalUserAvatar: c.userAvatarUrl ?? null,
    state: STATE_API_TO_FRONT[c.state] ?? 'bot',
    lastMessagePreview: lastMessageBody ?? c.lastMessageBody ?? c.summary ?? '',
    lastMessageAt: c.lastMessageAt,
    lastMessageKind: kind === 'comment' ? 'comment' : 'dm',
    slaRemainingSec,
    takenBy: c.currentHumanId,
    // unread = un humano no revisó la conv desde el último mensaje. Las
    // respuestas del bot NO la marcan leída (last_read_at solo lo toca un
    // humano al abrir el thread). 1 = no leída, 0 = leída.
    unread:
      !c.lastReadAt || new Date(c.lastMessageAt).getTime() > new Date(c.lastReadAt).getTime()
        ? 1
        : 0,
    activeSkillVersion: null,
  }
}

// ─── Messages ──────────────────────────────────────────────────────────────

export interface ApiIncomingMessage {
  id: number
  zernioMessageId: string | null
  conversationId: string | null
  receivedAt: string
  sentAt: string | null
  clientSlug: string
  platform: string
  kind: 'dm' | 'comment'
  zernioAccountId: string
  threadId: string | null
  zernioPostId: string | null
  commentId: string | null
  externalUserId: string | null
  externalUsername: string | null
  body: string
  mediaUrls: unknown
  state: string
  decisionRationale: string | null
  responseBody: string | null
  respondedAt: string | null
  respondedBy: string | null
  escalatedTo: string | null
}

/**
 * Una fila de cm_incoming_messages se expande en 1 o 2 mensajes para la UI:
 * 1) el mensaje entrante (siempre).
 * 2) el reply outgoing (si responseBody no es null) — origin se infiere de
 *    respondedBy: si arranca con "cm-ai" o es null pero state=responded →
 *    bot; sino humano.
 */
export function apiMessageToFrontend(row: ApiIncomingMessage): Message[] {
  const out: Message[] = []
  out.push({
    id: `${row.id}.in`,
    conversationId: row.conversationId ?? '',
    direction: 'in',
    origin: 'user',
    kind: row.kind,
    postId: row.zernioPostId,
    commentId: row.commentId,
    authorName: row.externalUsername ?? row.externalUserId,
    body: row.body,
    // Hora REAL de envío del usuario (sent_at). Fallback a received_at para
    // filas sin sent_at (fuentes sin timestamp / previas a la columna).
    createdAt: row.sentAt ?? row.receivedAt,
    attachments: extractAttachments(row.mediaUrls),
    isInternalNote: false,
  })

  if (row.responseBody) {
    const isBot = !row.respondedBy || row.respondedBy === 'cm-ai' || row.respondedBy === 'bot'
    out.push({
      id: `${row.id}.out`,
      conversationId: row.conversationId ?? '',
      direction: 'out',
      origin: isBot ? 'bot' : 'human',
      authorName: isBot ? 'CM AI' : row.respondedBy,
      body: row.responseBody,
      createdAt: row.respondedAt ?? row.receivedAt,
      attachments: [],
      isInternalNote: false,
    })
  }

  // Las notas internas se guardan inline en decisionRationale con prefijo
  // '[nota interna]'. Las extraemos acá para mostrarlas en el thread del
  // inbox como pseudo-mensaje (la fuente de verdad sigue siendo el audit log,
  // este render es solo UI).
  if (row.decisionRationale && row.decisionRationale.startsWith('[nota interna]')) {
    out.push({
      id: `${row.id}.note`,
      conversationId: row.conversationId ?? '',
      direction: 'out',
      origin: 'human',
      authorName: row.respondedBy ?? null,
      body: row.decisionRationale.replace(/^\[nota interna\]\s*/, ''),
      createdAt: row.respondedAt ?? row.receivedAt,
      attachments: [],
      isInternalNote: true,
    })
  }

  return out
}

// Respuesta humana reconstruida del audit log (action='human.reply_sent').
export interface ApiAuditHumanReply {
  id: number | string
  resourceId: string
  actorId: string
  createdAt: string
  payload: { body?: string; by?: string; dryRun?: boolean }
}

export function auditReplyToMessage(
  e: ApiAuditHumanReply,
  conversationId: string,
): Message {
  return {
    id: `audit-${e.id}`,
    conversationId,
    direction: 'out',
    origin: 'human',
    authorName: e.payload?.by ?? e.actorId ?? null,
    body: e.payload?.body ?? '',
    createdAt: e.createdAt,
    attachments: [],
    isInternalNote: false,
  }
}

function extractAttachments(media: unknown): Array<{ type: 'image' | 'video' | 'sticker'; url: string }> {
  if (!media) return []
  if (typeof media === 'string') {
    try {
      media = JSON.parse(media)
    } catch {
      return []
    }
  }
  if (!Array.isArray(media)) return []
  return media
    .filter(
      (m): m is { type: string; url: string } =>
        typeof m === 'object' && m !== null && 'url' in m,
    )
    .map((m) => ({
      type: (['image', 'video', 'sticker'].includes(m.type) ? m.type : 'image') as
        | 'image'
        | 'video'
        | 'sticker',
      url: m.url,
    }))
}

// ─── Alerts ────────────────────────────────────────────────────────────────

export interface ApiAlert {
  id: number
  kind: string
  clientSlug: string | null
  severity: 'low' | 'medium' | 'high' | 'critical'
  payload: Record<string, unknown>
  acknowledged: boolean
  acknowledgedBy: string | null
  acknowledgedAt: string | null
  createdAt: string
}

export function apiAlertToAlertEvent(a: ApiAlert): AlertEvent {
  return {
    id: String(a.id),
    level: a.severity,
    client: (a.clientSlug as ClientSlug | null) ?? null,
    title: a.kind,
    body:
      typeof a.payload['info'] === 'string'
        ? (a.payload['info'] as string)
        : JSON.stringify(a.payload),
    createdAt: a.createdAt,
    acknowledged: a.acknowledged,
  }
}

// ─── Audit ─────────────────────────────────────────────────────────────────

export interface ApiAuditEntry {
  id: number | bigint
  prevHash: string | null
  hash: string
  actorId: string
  actorRole: 'admin' | 'operator' | 'client' | 'external' | 'system'
  action: string
  resourceType: string
  resourceId: string
  payload: Record<string, unknown>
  clientSlug: string | null
  createdAt: string
}

export function apiAuditToAuditEntry(a: ApiAuditEntry): AuditEntry {
  // Frontend espera target type limitado. Mapeo best-effort:
  const targetType = mapAuditTargetType(a.resourceType)
  return {
    id: String(a.id),
    prevHash: a.prevHash ?? '',
    hash: a.hash,
    actor: a.actorId,
    actorRole: a.actorRole === 'system' ? 'admin' : (a.actorRole as UserRole),
    action: a.action,
    targetType,
    targetId: a.resourceId,
    client: (a.clientSlug as ClientSlug | null) ?? null,
    meta: a.payload,
    createdAt: a.createdAt,
  }
}

function mapAuditTargetType(rt: string): AuditEntry['targetType'] {
  if (rt.includes('conversation')) return 'conversation'
  if (rt.includes('skill')) return 'skill'
  if (rt.includes('account') || rt.includes('toggle')) return 'account'
  if (rt.includes('kill_switch')) return 'kill_switch'
  if (rt.includes('audit')) return 'audit_export'
  return 'account'
}

// ─── Accounts ──────────────────────────────────────────────────────────────

export interface ApiAccount {
  id: number
  clientSlug: string
  platform: string
  zernioProfileId: string
  zernioAccountId: string
  handle: string | null
  industry: string | null
  locale: string | null
  metaPageId: string | null
  tokenMode: 'champe' | 'client'
  createdAt: string
  updatedAt: string
}

export interface ApiToggle {
  id: number
  clientSlug: string
  platform: string
  publishingEnabled: boolean
  moderationEnabled: boolean
  dryRun: boolean
  killSwitchLevel: 'none' | 'bot_only' | 'bot_and_escalations' | 'full'
  updatedAt: string
}

export function apiAccountToAccount(a: ApiAccount, toggle?: ApiToggle): Account {
  return {
    id: String(a.id),
    client: a.clientSlug as ClientSlug,
    platform: a.platform as Platform,
    handle: a.handle ?? '',
    tokenMode: a.tokenMode,
    dryRun: toggle?.dryRun ?? false,
    enabled: toggle?.moderationEnabled ?? false,
    lastEventAt: null,
  }
}

// ─── Sandbox ───────────────────────────────────────────────────────────────

export interface ApiSandboxRun {
  id: string
  clientSlug: string
  skillVersion: string
  questionsCount: number
  processedCount: number
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled'
  triggeredBy: string
  createdAt: string
  finishedAt: string | null
  errorMessage: string | null
}

export function apiSandboxToSandboxRun(r: ApiSandboxRun): SandboxRun {
  // Front no expone 'cancelled' — lo mapeamos a failed para no romper la UI.
  const status: SandboxRun['status'] = r.status === 'cancelled' ? 'failed' : r.status
  return {
    id: r.id,
    client: r.clientSlug as ClientSlug,
    fileName: `${r.skillVersion}-${r.id.slice(0, 8)}`,
    totalQuestions: r.questionsCount,
    processed: r.processedCount,
    skillVersion: r.skillVersion,
    comparisonVersion: null,
    status,
    createdAt: r.createdAt,
    finishedAt: r.finishedAt,
    createdBy: r.triggeredBy,
  }
}
